import { parseLocalDate } from "@nova/format"

export type DatePreset = "today" | "7d" | "30d" | "thisYear" | "lastYear"

export type DateRangeValue = { preset: DatePreset } | { from: string; to: string }

export const datePresets: DatePreset[] = ["today", "7d", "30d", "thisYear", "lastYear"]

export const toISODate = (date: Date) => {
    const pad = (n: number) => n.toString().padStart(2, "0")
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export const datePresetLabel = (preset: DatePreset, now = new Date()) => {
    const year = now.getFullYear()
    switch (preset) {
        case "today":
            return "Today"
        case "7d":
            return "Last 7 days"
        case "30d":
            return "Last 30 days"
        case "thisYear":
            return `This year (${year})`
        case "lastYear":
            return `Last year (${year - 1})`
    }
}

const formatDisplayDate = (isoDate: string) => {
    const [year, month, day] = isoDate.split("-")
    return `${month}/${day}/${year}`
}

export const dateRangeLabel = (value: DateRangeValue, now = new Date()) =>
    "preset" in value
        ? datePresetLabel(value.preset, now)
        : `${formatDisplayDate(value.from)} - ${formatDisplayDate(value.to)}`

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

export const isValidDateRange = (value: DateRangeValue) =>
    !("from" in value) ||
    (ISO_DATE.test(value.from) && ISO_DATE.test(value.to) && value.from <= value.to)

const startOfToday = () => {
    const start = new Date()
    start.setHours(0, 0, 0, 0)
    return start
}

export const resolveDateRange = (value: DateRangeValue) => {
    if ("from" in value) {
        const from = parseLocalDate(value.from)
        const to = parseLocalDate(value.to)
        to.setHours(23, 59, 59, 999)
        return { start: from, end: to }
    }

    if (value.preset === "today") {
        const end = new Date()
        end.setMinutes(59, 59, 999)
        return { start: startOfToday(), end }
    }

    const end = new Date()
    end.setHours(23, 59, 59, 999)
    const start = startOfToday()

    if (value.preset === "7d") start.setDate(start.getDate() - 6)
    else if (value.preset === "30d") start.setDate(start.getDate() - 29)
    else if (value.preset === "thisYear") start.setMonth(0, 1)
    else if (value.preset === "lastYear") {
        start.setFullYear(start.getFullYear() - 1, 0, 1)
        const lastDay = new Date(start.getFullYear(), 11, 31)
        lastDay.setHours(23, 59, 59, 999)
        return { start, end: lastDay }
    }

    return { start, end }
}
